'use client';

import { useState } from 'react';
import {
  isCloudflareImageUrl,
  cfImage,
  cfImageSrcSet,
  SIZES,
  RESPONSIVE_BREAKPOINTS,
} from '@/lib/cloudflare-images';

type ImagePreset = keyof typeof RESPONSIVE_BREAKPOINTS;
type SizesPreset = keyof typeof SIZES;

interface CloudflareImageProps {
  src: string;
  alt: string;
  /**
   * Preset for responsive image sizing
   * - 'hero': Large featured images (640/1024/1366px)
   * - 'thumbnail': Blog listing cards (400/600/800px)
   * - 'card': Related posts (300/400/500px)
   * - 'content': Inline blog images (480/768/1024px)
   */
  preset?: ImagePreset;
  /**
   * Sizes attribute preset
   * - 'fullWidth': Full width on mobile, constrained on larger screens
   * - 'blogGrid': 3-column grid on desktop
   * - 'relatedPosts': 3-column grid
   * - 'content': Inline content width
   */
  sizesPreset?: SizesPreset;
  /**
   * Custom sizes attribute (overrides sizesPreset)
   */
  sizes?: string;
  /**
   * Fill the parent container (position: absolute, inset: 0)
   * Similar to Next.js Image fill prop
   */
  fill?: boolean;
  /**
   * Object fit style when using fill
   */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  /**
   * Loading priority
   */
  priority?: boolean;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Explicit width (when not using fill)
   */
  width?: number;
  /**
   * Explicit height (when not using fill)
   */
  height?: number;
  /**
   * On hover scale effect (for cards)
   */
  hoverScale?: boolean;
}

/**
 * Responsive image component for Cloudflare Images
 *
 * Automatically generates srcSet with multiple resolutions for
 * Cloudflare Images URLs. Falls back to standard img for other URLs.
 *
 * @example
 * // Featured image (fills parent)
 * <CloudflareImage
 *   src={post.featuredImage}
 *   alt={post.title}
 *   preset="hero"
 *   sizesPreset="fullWidth"
 *   fill
 *   priority
 * />
 *
 * @example
 * // Blog listing card
 * <CloudflareImage
 *   src={post.featuredImage}
 *   alt={post.title}
 *   preset="thumbnail"
 *   sizesPreset="blogGrid"
 *   fill
 *   hoverScale
 * />
 */
export default function CloudflareImage({
  src,
  alt,
  preset = 'content',
  sizesPreset = 'content',
  sizes: customSizes,
  fill = false,
  objectFit = 'cover',
  priority = false,
  className = '',
  width,
  height,
  hoverScale = false,
}: CloudflareImageProps) {
  const [hasError, setHasError] = useState(false);

  // Browsers resolve <img src=""> to the current document URL, producing
  // bogus 404s reported as "image failed to load" — refuse to render.
  if (!src || !src.trim()) return null;

  // Determine if this is a Cloudflare image
  const isCfImage = isCloudflareImageUrl(src);

  // Get the appropriate sizes attribute
  const sizesAttr = customSizes || SIZES[sizesPreset];

  // Build srcSet for Cloudflare images
  const srcSet = isCfImage ? cfImageSrcSet(src, preset) : undefined;

  // Get default src (largest size in preset for quality)
  const defaultSrc = isCfImage
    ? cfImage(src, RESPONSIVE_BREAKPOINTS[preset].desktop)
    : src;

  // Handle error - fallback to original src
  const handleError = () => {
    if (!hasError) {
      setHasError(true);
    }
  };

  // Build class names
  const imgClasses = [
    className,
    fill ? 'absolute inset-0 w-full h-full' : '',
    fill ? `object-${objectFit}` : '',
    hoverScale ? 'transition-transform duration-300 group-hover:scale-105' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={hasError ? src : defaultSrc}
      srcSet={hasError ? undefined : srcSet}
      sizes={srcSet ? sizesAttr : undefined}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : 'auto'}
      className={imgClasses}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      onError={handleError}
    />
  );
}
